import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/errors";
import { cn } from "@/lib/utils";
import AdminBreadcrumb from "../components/AdminBreadcrumb";
import { RateLimitDefaultsForm } from "../components/RateLimit/RateLimitDefaultsForm";
import {
  useGetRateLimitDefaults,
  useUpdateRateLimitDefaults,
} from "../api/rateLimit.queries";
import {
  useRateLimitForm,
  type RateLimitDefaultsSchema,
} from "../hooks/useRateLimitForm";
import { RATE_LIMIT_FIELDS } from "../constants/rateLimits";
import type {
  RateLimitDefaults,
  RateLimitDefaultsPayload,
} from "../types/rate-limit.types";

/** Only send fields that actually changed — the API leaves omitted fields untouched. */
function buildPayload(
  values: RateLimitDefaultsSchema,
  current: RateLimitDefaults | undefined,
): RateLimitDefaultsPayload {
  return RATE_LIMIT_FIELDS.reduce<RateLimitDefaultsPayload>((payload, key) => {
    if (!current || values[key] !== current[key]) {
      payload[key] = values[key];
    }
    return payload;
  }, {});
}

export default function RateLimitContainer() {
  const { data, isLoading, isError, error } = useGetRateLimitDefaults();
  const { mutate: updateDefaults, isPending } = useUpdateRateLimitDefaults();

  const form = useRateLimitForm();
  const { reset, formState } = form;

  // Keep a stable reference to the values last received from the server so the
  // diff below is not affected by the form's own dirty state.
  const serverValuesRef = useRef<RateLimitDefaults | undefined>(undefined);

  useEffect(() => {
    if (!data) return;
    serverValuesRef.current = data;
    reset(data);
  }, [data, reset]);

  const handleSubmit = form.handleSubmit((values) => {
    const payload = buildPayload(values, serverValuesRef.current);

    if (Object.keys(payload).length === 0) {
      toast.info("No changes to save");
      return;
    }

    updateDefaults(payload);
  });

  const handleReset = () => {
    if (serverValuesRef.current) reset(serverValuesRef.current);
  };

  const isDirty = formState.isDirty;

  return (
    <main className="scrollbar-thin flex min-h-0 flex-1 flex-col gap-7 overflow-y-auto px-4 transition-all duration-300 md:px-16 lg:px-[120px]">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <AdminBreadcrumb title="Rate Limits" />

        {!isLoading && !isError && (
          <div className="flex flex-row items-center gap-3">
            <span
              className={cn(
                "flex items-center gap-2 text-xs leading-none font-medium transition-opacity duration-200",
                isDirty ? "text-brand-600 opacity-100" : "opacity-0",
              )}
            >
              <i className="ri-error-warning-line text-[14px]" />
              Unsaved changes
            </span>
            <Button
              icon
              variant="default"
              className="bg-black-950 hover:bg-black-950/80 flex h-[52px] w-auto flex-row gap-2 p-3"
              disabled={!isDirty || isPending}
              onClick={handleReset}
            >
              <i className="ri-arrow-go-back-line text-md font-light" />
              Reset
            </Button>
            <Button
              variant="default"
              className="disabled:bg-play h-[52px] w-auto p-3"
              disabled={!isDirty || isPending}
              onClick={handleSubmit}
            >
              {isPending ? (
                <Spinner size="sm" className="stroke-white!" />
              ) : (
                <i className="ri-save-line text-md font-light" />
              )}
              Save Changes
            </Button>
          </div>
        )}
      </div>

      <div className="min-h-0 flex-1">
        <div className="grid min-h-0 auto-rows-max gap-4 pb-20">
          {isLoading ? (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div
                  key={index}
                  className="bg-black-950 flex flex-col gap-4 rounded-lg p-5"
                >
                  <div className="flex items-center gap-3">
                    <Skeleton className="h-9 w-9 rounded-lg bg-white/10" />
                    <div className="flex flex-col gap-2">
                      <Skeleton className="h-3 w-32 bg-white/10" />
                      <Skeleton className="h-2 w-20 bg-white/10" />
                    </div>
                  </div>
                  <Skeleton className="h-[64px] w-full bg-white/10" />
                  <Skeleton className="h-8 w-full bg-white/10" />
                  <Skeleton className="h-3 w-full bg-white/10" />
                </div>
              ))}
            </div>
          ) : isError ? (
            <div className="bg-black-950 flex flex-col items-start gap-2 rounded-lg p-5">
              <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-red-500/10">
                <i className="ri-error-warning-line text-[16px] text-red-500" />
              </span>
              <div className="text-lg leading-5 font-bold -tracking-wide text-white">
                Unable to load rate limits
              </div>
              <p className="text-black-500 text-xs leading-4">
                {extractErrorMessage(error)}
              </p>
            </div>
          ) : (
            <RateLimitDefaultsForm
              form={form}
              savedValues={data}
              disabled={isPending}
            />
          )}
        </div>
      </div>
    </main>
  );
}
